import type { DefaultLabel } from 'state/persisted/request-create/types';

export type LabelsPublicValue = string[];
export type LabelsInternalValue = DefaultLabel[];

/**
 * Validates that a value conforms to the expected LabelsPublicValue format
 * @param value - The value to validate (can be any type)
 *                Input format: Should be an array of non-empty strings
 *                Valid examples: ["bug", "feature"], ["urgent", "frontend", "api"], []
 *                Invalid examples: null, undefined, ["", "bug"], [123, "bug"], "bug"
 * @returns Boolean indicating whether the value is a valid LabelsPublicValue
 *          Returns true if value is an array of non-empty strings, false otherwise
 */
export const labelsSetValueValidator = (value: unknown): value is LabelsPublicValue =>
    Array.isArray(value) && value.every((label) => typeof label === 'string' && label !== '');

/**
 * Transforms labels from public API format to internal component format (DefaultLabel[])
 * @param publicValue - The public API labels value to transform
 *                      Input format: Array of label strings (e.g., ["bug", "feature", "urgent"])
 * @returns Internal format labels array for component consumption
 *          Output format: Array of DefaultLabel objects with label and value properties
 *          Example: ["bug", "feature"] → [{ label: "bug", value: "bug" }, { label: "feature", value: "feature" }]
 */
export const labelsSetValueTransformer = (publicValue: LabelsPublicValue): LabelsInternalValue =>
    publicValue.map((label) => ({ label, value: label }));

/**
 * Transforms labels from internal component format (DefaultLabel[]) back to public API format
 * @param internalValue - The internal component labels value to transform
 *                        Input format: Array of DefaultLabel objects with label and value properties
 *                        Example: [{ label: "bug", value: "bug" }, { label: "feature", value: "feature" }]
 * @returns Public API format labels array
 *          Output format: Array of label strings (e.g., ["bug", "feature", "urgent"])
 *          Example: [{ label: "bug", value: "bug" }, { label: "feature", value: "feature" }] → ["bug", "feature"]
 */
export const labelsGetValueTransformer = (internalValue: LabelsInternalValue): LabelsPublicValue =>
    internalValue.map(({ value }) => value);

/**
 * Transforms labels internal data to basic string array format for UI modifications
 * @param data - The internal labels data to transform
 *               Input format: Array of DefaultLabel objects or any value
 *               Example: [{ label: "bug", value: "bug" }, { label: "feature", value: "feature" }]
 * @returns Basic string array format for UI modifications
 *          Output format: Array of label value strings
 *          Example: [{ label: "bug", value: "bug" }, { label: "feature", value: "feature" }] → ["bug", "feature"]
 *          Returns empty array if input is not a valid array
 */
export const modifyLabelsValueToBasic = (data: LabelsInternalValue): string[] => {
    if (Array.isArray(data)) {
        return data.map((item) => item.value);
    }
    return [];
};
